import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Table,
  Row,
  Col,
  Alert,
  ProgressBar,
  Badge,
  Form,
  Spinner,
  Toast,
  ToastContainer,
   Button,  // ✅ Add this
  Modal  
} from "react-bootstrap";
import axios from "axios";

const AvoidableLossCalculator = ({ assets = [], currency = "USD", userData }) => {
  const [riskMitigationFactor, setRiskMitigationFactor] = useState(70);
  const [calculatedMetrics, setCalculatedMetrics] = useState([]);
  const [totalMetrics, setTotalMetrics] = useState({});
  const [saving, setSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState("success");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
const [deleting, setDeleting] = useState(false);


  const currentUserId = userData?.["cognito:username"];
  const currentUserEmail = userData?.email;
  const region = sessionStorage.getItem("userRegion") || "IN";

  // ✅ Track if this is the first render (to avoid saving on mount)
  const isFirstRender = useRef(true);
  const saveTimeoutRef = useRef(null);

  const SLIDER_MIN = 0;
  const SLIDER_MAX = 100;
  const AUTO_SAVE_DELAY = 2000; // Auto-save after 2 seconds of inactivity

  // ✅ API endpoint
  const SAVE_CALCULATION_API = "https://0d268zx4x5.execute-api.ap-southeast-2.amazonaws.com/prod/api/avoidable-loss/save";
  const DELETE_CALCULATION_API = "https://o1hm27cgme.execute-api.ap-southeast-2.amazonaws.com/prod/api/avoidable-loss/delete-calculations";


  const triggerToast = (message, variant = "success") => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null || value === "") return "N/A";
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return num.toLocaleString(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // ✅ Calculate metrics whenever assets or mitigation factor changes
  useEffect(() => {
    const metrics = assets.map((asset, index) => {
      const assetValue = Number(asset.assetValue) || 0;
      const exposureFactor = Number(asset.exposureFactor) || 50;
      const aro = Number(asset.annualizedRateOfOccurrence) || 0.2;

      // Core Risk Calculations
      const sle = assetValue * (exposureFactor / 100);
      const ale = sle * aro;
      const maxPossibleLoss = assetValue;

      // Avoidable Loss Calculations
      const avoidableLossWithMitigation = ale * (riskMitigationFactor / 100);
      const residualRisk = ale - avoidableLossWithMitigation;

      return {
        index: index + 1,
        assetName: asset.assetName,
        assetValue,
        exposureFactor,
        aro,
        sle,
        ale,
        maxPossibleLoss,
        avoidableLossWithMitigation,
        residualRisk,
        riskLevel: ale > assetValue * 0.1 ? "High" : ale > assetValue * 0.05 ? "Medium" : "Low"
      };
    });

    const totals = {
      totalAssetValue: metrics.reduce((sum, m) => sum + m.assetValue, 0),
      totalSLE: metrics.reduce((sum, m) => sum + m.sle, 0),
      totalALE: metrics.reduce((sum, m) => sum + m.ale, 0),
      totalMaxPossibleLoss: metrics.reduce((sum, m) => sum + m.maxPossibleLoss, 0),
      totalAvoidableLoss: metrics.reduce((sum, m) => sum + m.avoidableLossWithMitigation, 0),
      totalResidualRisk: metrics.reduce((sum, m) => sum + m.residualRisk, 0)
    };

    setCalculatedMetrics(metrics);
    setTotalMetrics(totals);
  }, [assets, riskMitigationFactor]);

  // ✅ FIXED: Auto-save when assets or calculations change (with debounce)
  useEffect(() => {
    // Skip auto-save on first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      console.log("🔵 First render - skipping auto-save");
      return;
    }

    // Skip if no assets
    if (assets.length === 0) {
      console.log("⚠️ No assets - skipping auto-save");
      return;
    }

    // Skip if user info not available
    if (!currentUserId || !currentUserEmail) {
      console.log("⚠️ User info not available - skipping auto-save");
      return;
    }

    console.log("🔄 Change detected - scheduling auto-save in 2 seconds...");

    // Clear previous timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      console.log("⏱️ Clearing previous timeout");
    }

    // Set new timeout for auto-save (debounce)
    saveTimeoutRef.current = setTimeout(() => {
      console.log("💾 Executing auto-save NOW");
      autoSaveCalculation();
    }, AUTO_SAVE_DELAY);

    // Cleanup on unmount
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [assets, calculatedMetrics, totalMetrics, riskMitigationFactor, currentUserId, currentUserEmail]);

  // ✅ FIXED: Auto-save function with proper dependencies
  const autoSaveCalculation = async () => {
   

    if (!currentUserId || !currentUserEmail) {
      console.log("❌ Missing user info");
      return;
    }

    if (assets.length === 0) {
      console.log("❌ No assets to save");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        userId: currentUserId,
        email: currentUserEmail,
        currency,
        riskMitigationFactor,
        assets,
        calculatedMetrics,
        totalMetrics,
      };

      console.log("📤 Sending POST request:", payload);

      const response = await axios.post(SAVE_CALCULATION_API, payload, {
        headers: {
          Region: region,
          'Content-Type': 'application/json'
        },
      });

      console.log("✅ Auto-saved calculation:", response.data);
      triggerToast("✅ Calculation auto-saved", "success");
    } catch (error) {
      console.error("❌ Error auto-saving calculation:", error);
      console.error("Error details:", error.response?.data);
      triggerToast("⚠️ Auto-save failed", "danger");
    } finally {
      setSaving(false);
    }
  };

  const getRiskBadgeVariant = (riskLevel) => {
    switch (riskLevel) {
      case "High": return "danger";
      case "Medium": return "warning";
      case "Low": return "success";
      default: return "secondary";
    }
  };

  if (assets.length === 0) {
    return (
      <Card className="p-4 shadow-sm">
        <Alert variant="info">
          📊 No assets available for loss calculation. Please add assets first.
        </Alert>
      </Card>
    );
  }
// ✅ Delete all calculations for current user
const handleDeleteAllCalculations = async () => {
  if (!currentUserId || !region) {
    triggerToast("❌ Missing user information", "danger");
    return;
  }

  setDeleting(true);
  try {
    console.log("Deleting calculations for userId:", currentUserId, "region:", region);

    const response = await axios.delete(DELETE_CALCULATION_API, {
      params: {
        userId: currentUserId,
        region: region
      }
    });

    console.log("✅ Delete response:", response.data);

    if (response.data.success) {
      triggerToast(
        `✅ Successfully deleted ${response.data.deletedCount} calculation(s)`,
        "success"
      );
      
      // Optionally reset the UI state
      setRiskMitigationFactor(70);
      setCalculatedMetrics([]);
      setTotalMetrics({});
      
    } else {
      triggerToast("⚠️ Failed to delete calculations", "warning");
    }

    setShowDeleteModal(false);
  } catch (error) {
    console.error("❌ Error deleting calculations:", error);
    
    if (error.response?.status === 404) {
      triggerToast("ℹ️ No calculations found to delete", "info");
    } else {
      triggerToast(
        `❌ Error: ${error.response?.data?.message || error.message}`,
        "danger"
      );
    }
    
    setShowDeleteModal(false);
  } finally {
    setDeleting(false);
  }
};

  return (
    <div>
      {/* Toast Notifications */}
      <ToastContainer position="top-end" className="p-3">
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={3000}
          autohide
          bg={toastVariant}
        >
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Auto-save Indicator */}
     {/* Auto-save Indicator with Delete Button */}
<Card className="p-2 shadow-sm mb-3 border-0" style={{ backgroundColor: '#f8f9fa' }}>
  <div className="d-flex justify-content-between align-items-center">
    <div className="d-flex align-items-center gap-3">
      <div>
        <small className="text-muted">
          <i className="fas fa-sync-alt me-1"></i>
          Auto-save enabled {currentUserId ? `(User: ${currentUserId})` : '(No user)'}
        </small>
      </div>
      {saving && (
        <div className="d-flex align-items-center">
          <Spinner size="sm" animation="border" className="me-2" />
          <small className="text-primary">Saving...</small>
        </div>
      )}
    </div>
    
    {/* Delete Button */}
    <Button
      variant="outline-danger"
      size="sm"
      onClick={() => setShowDeleteModal(true)}
      disabled={deleting || assets.length === 0}
      className="d-flex align-items-center gap-1"
    >
      <i className="fas fa-trash-alt"></i>
      Clear All Calculations
    </Button>
  </div>
</Card>


      {/* Risk Mitigation Control */}
      <Card className="p-4 shadow-sm mb-4 border-0">
        <h5>
          <i className="fas fa-shield-alt me-2 text-success"></i>
          🛡️ Risk Mitigation Settings
        </h5>
        <Row className="align-items-center">
          <Col md={8}>
            <Form.Label className="fw-bold">
              Risk Mitigation Effectiveness: {riskMitigationFactor}%
            </Form.Label>

            {/* Quick selection buttons */}
            <div className="mb-3">
              <small className="text-muted d-block mb-2">Quick Select:</small>
              <div className="btn-group btn-group-sm" role="group">
                {[25, 50, 70, 85, 95].map(value => (
                  <button
                    key={value}
                    type="button"
                    className={`btn ${riskMitigationFactor === value ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setRiskMitigationFactor(value)}
                  >
                    {value}%
                  </button>
                ))}
              </div>
            </div>

            <Form.Range
              min={SLIDER_MIN}
              max={SLIDER_MAX}
              step={1}
              value={riskMitigationFactor}
              onChange={(e) => setRiskMitigationFactor(Number(e.target.value))}
            />
            <div className="d-flex justify-content-between text-muted small">
              <span>0% (No mitigation)</span>
              <span>50%</span>
              <span>100% (Theoretical)</span>
            </div>
          </Col>
          <Col md={4}>
            <Alert variant="info" className="mb-0 small">
              <i className="fas fa-info-circle me-1"></i>
              This represents how much risk can be reduced through security controls,
              training, and other mitigation measures.
            </Alert>
          </Col>
        </Row>
      </Card>

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center h-100 border-primary shadow-sm">
            <Card.Body>
              <div className="mb-2">
                <i className="fas fa-dollar-sign fa-2x text-primary"></i>
              </div>
              <h6 className="text-primary">💰 Total Asset Value</h6>
              <h4 className="text-primary">{formatCurrency(totalMetrics.totalAssetValue)}</h4>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center h-100 border-danger shadow-sm">
            <Card.Body>
              <div className="mb-2">
                <i className="fas fa-exclamation-triangle fa-2x text-danger"></i>
              </div>
              <h6 className="text-danger">⚠️ Annual Loss Expectancy</h6>
              <h4 className="text-danger">{formatCurrency(totalMetrics.totalALE)}</h4>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center h-100 border-success shadow-sm">
            <Card.Body>
              <div className="mb-2">
                <i className="fas fa-shield-alt fa-2x text-success"></i>
              </div>
              <h6 className="text-success">🛡️ Avoidable Loss</h6>
              <h4 className="text-success">{formatCurrency(totalMetrics.totalAvoidableLoss)}</h4>
              <small className="text-muted">With {riskMitigationFactor}% mitigation</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center h-100 border-warning shadow-sm">
            <Card.Body>
              <div className="mb-2">
                <i className="fas fa-bullseye fa-2x text-warning"></i>
              </div>
              <h6 className="text-warning">🎯 Residual Risk</h6>
              <h4 className="text-warning">{formatCurrency(totalMetrics.totalResidualRisk)}</h4>
              <small className="text-muted">After mitigation</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Detailed Loss Analysis Table */}
      <Card className="p-4 shadow-sm mb-4 border-0">
        <h5>
          <i className="fas fa-table me-2 text-info"></i>
          📊 Detailed Loss Analysis
        </h5>
        <div className="table-responsive">
          <Table striped bordered hover size="sm">
            <thead className="table-dark">
              <tr>
                <th>#</th>
                <th>Asset Name</th>
                <th>Asset Value</th>
                <th>SLE</th>
                <th>ALE</th>
                <th>Avoidable Loss</th>
                <th>Residual Risk</th>
                <th>Risk Level</th>
                <th>ROI Potential</th>
              </tr>
            </thead>
            <tbody>
              {calculatedMetrics.map((metric) => {
                const roiPotential = metric.avoidableLossWithMitigation > 0
                  ? ((metric.avoidableLossWithMitigation / metric.assetValue) * 100).toFixed(1)
                  : "0.0";

                return (
                  <tr key={metric.index}>
                    <td>{metric.index}</td>
                    <td className="fw-bold">{metric.assetName}</td>
                    <td>{formatCurrency(metric.assetValue)}</td>
                    <td>{formatCurrency(metric.sle)}</td>
                    <td>{formatCurrency(metric.ale)}</td>
                    <td className="text-success fw-bold">
                      {formatCurrency(metric.avoidableLossWithMitigation)}
                    </td>
                    <td className="text-warning">
                      {formatCurrency(metric.residualRisk)}
                    </td>
                    <td>
                      <Badge bg={getRiskBadgeVariant(metric.riskLevel)}>
                        {metric.riskLevel}
                      </Badge>
                    </td>
                    <td>{roiPotential}%</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="table-dark">
                <th colSpan="2">TOTALS</th>
                <th>{formatCurrency(totalMetrics.totalAssetValue)}</th>
                <th>{formatCurrency(totalMetrics.totalSLE)}</th>
                <th>{formatCurrency(totalMetrics.totalALE)}</th>
                <th className="text-success">{formatCurrency(totalMetrics.totalAvoidableLoss)}</th>
                <th className="text-warning">{formatCurrency(totalMetrics.totalResidualRisk)}</th>
                <th colSpan="2">
                  <Badge bg="info">
                    {((totalMetrics.totalAvoidableLoss / totalMetrics.totalALE) * 100).toFixed(1)}% Avoidable
                  </Badge>
                </th>
              </tr>
            </tfoot>
          </Table>
        </div>
      </Card>

      {/* Risk Visualization */}
      <Card className="p-4 shadow-sm border-0">
        <h5>
          <i className="fas fa-chart-bar me-2 text-success"></i>
          📈 Risk Distribution
        </h5>
        <Row>
          <Col md={6}>
            <h6>Annual Loss Expectancy Breakdown</h6>
            <div className="mb-3">
              <div className="d-flex justify-content-between">
                <span>Avoidable Loss</span>
                <span className="text-success fw-bold">
                  {formatCurrency(totalMetrics.totalAvoidableLoss)}
                </span>
              </div>
              <ProgressBar
                variant="success"
                now={(totalMetrics.totalAvoidableLoss / totalMetrics.totalALE) * 100}
                className="mb-2"
              />
            </div>
            <div className="mb-3">
              <div className="d-flex justify-content-between">
                <span>Residual Risk</span>
                <span className="text-warning fw-bold">
                  {formatCurrency(totalMetrics.totalResidualRisk)}
                </span>
              </div>
              <ProgressBar
                variant="warning"
                now={(totalMetrics.totalResidualRisk / totalMetrics.totalALE) * 100}
              />
            </div>
          </Col>
          <Col md={6}>
            <Alert variant="info">
              <h6>💡 Key Insights</h6>
              <ul className="mb-0">
                <li>
                  <strong>{((totalMetrics.totalAvoidableLoss / totalMetrics.totalALE) * 100).toFixed(1)}%</strong> of annual risk can be mitigated
                </li>
                <li>
                  Potential annual savings: <strong>{formatCurrency(totalMetrics.totalAvoidableLoss)}</strong>
                </li>
                <li>
                  Risk exposure ratio: <strong>{((totalMetrics.totalALE / totalMetrics.totalAssetValue) * 100).toFixed(2)}%</strong> of total asset value
                </li>
              </ul>
            </Alert>
          </Col>
        </Row>
      </Card>
       <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton className="border-0">
          <Modal.Title>
            <i className="fas fa-exclamation-triangle text-warning me-2"></i>
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="mb-3">
            <i className="fas fa-info-circle me-2"></i>
            <strong>Warning:</strong> This action cannot be undone!
          </Alert>
          
          <p className="mb-2">
            Are you sure you want to delete <strong>all saved calculations</strong> for:
          </p>
          
          <div className="bg-light p-3 rounded mb-3">
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted">User ID:</span>
              <span className="fw-bold">{currentUserId}</span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <span className="text-muted">Email:</span>
              <span className="fw-bold">{currentUserEmail}</span>
            </div>
            <div className="d-flex justify-content-between">
              <span className="text-muted">Region:</span>
              <Badge bg="primary">{region}</Badge>
            </div>
          </div>

          <p className="text-muted small mb-0">
            This will remove all historical calculation data from the database.
            Your current assets will not be affected.
          </p>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={deleting}
          >
            <i className="fas fa-times me-1"></i>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteAllCalculations}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Deleting...
              </>
            ) : (
              <>
                <i className="fas fa-trash-alt me-1"></i>
                Yes, Delete All
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AvoidableLossCalculator;
